import BigNumber from "bignumber.js"
import ReactEcharts, { EChartsInstance } from "echarts-for-react"
import { Modal, Row } from "react-bootstrap"
import {
  average as findAverage,
  max as findMax,
  min as findMin,
} from "simple-statistics"
import { getSpeedChartOptions } from "../helpers/getSpeedChartOptionsNew"
import { resolveClickedLeg } from "../helpers/resolveClickedLeg"
import { useFetchVesselLegs } from "../hooks/useFetchVesselLegs"
import { SpeedChartClickEvent } from "./PrevetVesselSpeedChart"
import { VesselLeg } from "../hooks/usePrevetAPI"
import { useEffect, useRef, useState } from "react"
import { SpeedChartStatsHeader } from "./SpeedChartStatsHeader"
import { usePrevetTabStateContext } from "../hooks/prevetContext"
import { PrevetZoneAvgPopover } from "./PrevetZoneAvgPopover"

interface PrevetSpeedGraphModalProps {
  vesselName: string
  isSpeedGraphModalShown: boolean
  closeSpeedGraphModal: () => void
  vesselImo: number
  userLadenVesselSpeed: string | undefined
  userBallastVesselSpeed: string | undefined
  setIsTradingModalShown: React.Dispatch<React.SetStateAction<boolean>>
  setChartLadenStatus: React.Dispatch<React.SetStateAction<boolean | undefined>>
  setDateForCenterMarker: React.Dispatch<
    React.SetStateAction<undefined | string>
  >
  endDate: string
  startDate: string
  ladenMin: number
  ballastMin: number
  globalMax: number
  ladenLegsData: VesselLeg[]
  ballastLegsData: VesselLeg[]
  describedBallastSpeed: number
  describedLadenSpeed: number
  isShowSeaMarginChecked: boolean
}

interface ChartStats {
  min: number
  max: number
  avg: number
  apiSeaMarginFraction: number
}

function computeStats(legs: VesselLeg[]): ChartStats {
  if (legs.length === 0)
    return { min: 0, max: 0, avg: 0, apiSeaMarginFraction: 0 }
  return {
    min: findMin(legs.map((l) => l.sog)),
    max: findMax(legs.map((l) => l.sog)),
    avg: BigNumber(findAverage(legs.map((l) => l.sog)))
      .decimalPlaces(1, BigNumber.ROUND_HALF_CEIL)
      .toNumber(),
    apiSeaMarginFraction:
      findAverage(legs.map((l) => l.margin ?? 0)) || 0,
  }
}

const HEADER_HEIGHT = 48

export function PrevetSpeedGraphModal({
  vesselName,
  vesselImo,
  isSpeedGraphModalShown,
  closeSpeedGraphModal,
  userLadenVesselSpeed,
  userBallastVesselSpeed,
  setIsTradingModalShown,
  setDateForCenterMarker,
  setChartLadenStatus,
  endDate,
  startDate,
  ladenMin,
  ballastMin,
  globalMax,
  ladenLegsData,
  ballastLegsData,
  describedBallastSpeed,
  describedLadenSpeed,
  isShowSeaMarginChecked,
}: PrevetSpeedGraphModalProps) {
  const monthsAgoFromToday = startDate == "6 Months" ? 6 : 12
  const { vesselLegsData } = useFetchVesselLegs({
    imo: vesselImo,
    end: endDate,
    start: startDate,
  })
  const { setTabStateValue, getTabStateValue } = usePrevetTabStateContext()

  const ladenLegs = ladenLegsData
  const ballastLegs = ballastLegsData

  const ladenChartRef = useRef<any>(null)
  const ballastChartRef = useRef<any>(null)

  const [avgLadenSpeed, setAvgLadenSpeed] = useState<number>(0)
  const [avgBallastSpeed, setAvgBallastSpeed] = useState<number>(0)
  const [ladenStats, setLadenStats] = useState<ChartStats>(() =>
    computeStats(ladenLegs)
  )
  const [ballastStats, setBallastStats] = useState<ChartStats>(() =>
    computeStats(ballastLegs)
  )
  // Inclusive index ranges of the legs visible after zooming each chart.
  const [ladenRange, setLadenRange] = useState<[number, number]>([0, ladenLegs.length - 1])
  const [ballastRange, setBallastRange] = useState<[number, number]>([0, ballastLegs.length - 1])

  const ladenMarginOverride = getTabStateValue(
    "ladenMarginOverride",
    undefined
  ) as number | undefined
  const ballastMarginOverride = getTabStateValue(
    "ballastMarginOverride",
    undefined
  ) as number | undefined

  useEffect(() => {
    if (!ladenLegs || !ballastLegs) return
    const avgLaden = findAverage(ladenLegs.map((leg) => leg.sog))
    const avgBallast = findAverage(ballastLegs.map((leg) => leg.sog))

    setAvgLadenSpeed(
      BigNumber(avgLaden).decimalPlaces(1, BigNumber.ROUND_HALF_CEIL).toNumber()
    )
    setAvgBallastSpeed(
      BigNumber(avgBallast)
        .decimalPlaces(1, BigNumber.ROUND_HALF_CEIL)
        .toNumber()
    )
    setLadenStats(computeStats(ladenLegs))
    setBallastStats(computeStats(ballastLegs))
    setLadenRange([0, ladenLegs.length - 1])
    setBallastRange([0, ballastLegs.length - 1])
  }, [ladenLegs, ballastLegs])

  if (!vesselLegsData) {
    return <></>
  }

  const openTradeMap = (selectedLeg: VesselLeg, isLaden: boolean) => {
    setIsTradingModalShown(true)
    setDateForCenterMarker(selectedLeg.pos_date)
    setChartLadenStatus(isLaden)
  }

  const effectiveLadenMargin =
    ladenMarginOverride ?? ladenStats.apiSeaMarginFraction
  const effectiveBallastMargin =
    ballastMarginOverride ?? ballastStats.apiSeaMarginFraction

  const ladenSmPct = ladenMarginOverride != null ? Number((ladenMarginOverride * 100).toFixed(1)) : null
  const ladenData = getSpeedChartOptions({
    vesselName,
    speedDataRaw: ladenLegs,
    ladenStatus: "laden",
    months: monthsAgoFromToday,
    showAllZones: true,
    userVesselSpeed: userLadenVesselSpeed,
    globalMin: ladenMin,
    globalMax: globalMax,
    avgSpeed: avgLadenSpeed,
    descSpeed: describedLadenSpeed,
    showSeaMargin: isShowSeaMarginChecked,
    hideTitle: true,
    manualSeaMarginPct: ladenSmPct,
  })
  if (ladenData?.series) {
    ;(ladenData.series as any[]).push({
      name: "Manual Sea Margin",
      type: "line",
      xAxisIndex: 1,
      yAxisIndex: 1,
      showSymbol: false,
      color: "transparent",
      silent: true,
      tooltip: { show: false },
      markLine: {
        symbol: ["none"],
        lineStyle: { color: "red", type: "dotted", width: 2 },
        data:
          ladenSmPct != null
            ? [
                {
                  yAxis: ladenSmPct,
                  label: { show: false },
                },
              ]
            : [],
      },
    })
  }

  const ballastSmPct = ballastMarginOverride != null ? Number((ballastMarginOverride * 100).toFixed(1)) : null
  const ballastData = getSpeedChartOptions({
    vesselName,
    speedDataRaw: ballastLegs,
    ladenStatus: "ballast",
    months: monthsAgoFromToday,
    showAllZones: true,
    userVesselSpeed: userBallastVesselSpeed,
    globalMin: ballastMin,
    globalMax: globalMax,
    avgSpeed: avgBallastSpeed,
    descSpeed: describedBallastSpeed,
    showSeaMargin: isShowSeaMarginChecked,
    hideTitle: true,
    manualSeaMarginPct: ballastSmPct,
  })
  if (ballastData?.series) {
    ;(ballastData.series as any[]).push({
      name: "Manual Sea Margin",
      type: "line",
      xAxisIndex: 1,
      yAxisIndex: 1,
      showSymbol: false,
      color: "transparent",
      silent: true,
      tooltip: { show: false },
      markLine: {
        symbol: ["none"],
        lineStyle: { color: "red", type: "dotted", width: 2 },
        data:
          ballastSmPct != null
            ? [
                {
                  yAxis: ballastSmPct,
                  label: { show: false },
                },
              ]
            : [],
      },
    })
  }

  return (
    <Modal size="xl" show={isSpeedGraphModalShown}>
      <div className="modal-header">
        <h5 className="modal-title mt-0" id="OverviewModalLabel">
          Speed Chart: {vesselName} ({vesselImo})
        </h5>
        <button
          onClick={() => {
            closeSpeedGraphModal()
          }}
          type="button"
          className="btn-close"
          data-dismiss="modal"
          aria-label="Close"
        />
      </div>
      <div className="PortPredictor_SpeedGraphModal">
        <Row>
          <SpeedChartStatsHeader
            isLaden={true}
            months={monthsAgoFromToday}
            min={ladenStats.min}
            max={ladenStats.max}
            avg={ladenStats.avg}
            seaMarginFraction={effectiveLadenMargin}
            isManualOverride={ladenMarginOverride !== undefined}
            descSpeed={describedLadenSpeed}
            isShowSeaMarginChecked={isShowSeaMarginChecked}
            onMarginChange={(fraction) =>
              setTabStateValue("ladenMarginOverride", fraction, undefined)
            }
            onMarginReset={() =>
              setTabStateValue("ladenMarginOverride", undefined, undefined)
            }
            statsExtra={
              <PrevetZoneAvgPopover
                speedData={ladenLegs}
                vesselName={vesselName}
                visibleStartIndex={ladenRange[0]}
                visibleEndIndex={ladenRange[1]}
                onSelectLeg={(leg) => openTradeMap(leg, true)}
              />
            }
          />
          <ReactEcharts
            ref={ladenChartRef}
            style={{
              height: `${348 - HEADER_HEIGHT}px`,
            }}
            className="PrevetVesselSpeedChart"
            option={ladenData}
            onEvents={{
              click: (event: SpeedChartClickEvent, echart: EChartsInstance) => {
                const selectedLeg = resolveClickedLeg(event, echart, ladenLegs)
                if (!selectedLeg) return
                openTradeMap(selectedLeg, true)
              },
              dataZoom: (_event: any, echart: EChartsInstance) => {
                const opts = echart.getOption()
                const startIndex = opts.dataZoom[0].startValue
                const endIndex = opts.dataZoom[0].endValue
                const sliced = ladenLegs.slice(startIndex, endIndex + 1)
                setLadenRange([startIndex, endIndex])
                setLadenStats(computeStats(sliced))
              },
            }}
          />
        </Row>
        <Row>
          <SpeedChartStatsHeader
            isLaden={false}
            months={monthsAgoFromToday}
            min={ballastStats.min}
            max={ballastStats.max}
            avg={ballastStats.avg}
            seaMarginFraction={effectiveBallastMargin}
            isManualOverride={ballastMarginOverride !== undefined}
            descSpeed={describedBallastSpeed}
            isShowSeaMarginChecked={isShowSeaMarginChecked}
            onMarginChange={(fraction) =>
              setTabStateValue("ballastMarginOverride", fraction, undefined)
            }
            onMarginReset={() =>
              setTabStateValue("ballastMarginOverride", undefined, undefined)
            }
            statsExtra={
              <PrevetZoneAvgPopover
                speedData={ballastLegs}
                vesselName={vesselName}
                visibleStartIndex={ballastRange[0]}
                visibleEndIndex={ballastRange[1]}
                onSelectLeg={(leg) => openTradeMap(leg, false)}
              />
            }
          />
          <ReactEcharts
            ref={ballastChartRef}
            style={{
              height: `${348 - HEADER_HEIGHT}px`,
            }}
            className="PrevetVesselSpeedChart"
            option={ballastData}
            onEvents={{
              click: (event: SpeedChartClickEvent, echart: EChartsInstance) => {
                const selectedLeg = resolveClickedLeg(event, echart, ballastLegs)
                if (!selectedLeg) return
                openTradeMap(selectedLeg, false)
              },
              dataZoom: (_event: any, echart: EChartsInstance) => {
                const opts = echart.getOption()
                const startIndex = opts.dataZoom[0].startValue
                const endIndex = opts.dataZoom[0].endValue
                // Index into the same legs the chart was built from.
                const slicedBallast = ballastLegs.slice(startIndex, endIndex + 1)
                setBallastRange([startIndex, endIndex])
                setBallastStats(computeStats(slicedBallast))
              },
            }}
          />
        </Row>
      </div>
    </Modal>
  )
}
